'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TEAM_PATHNAME } from '@/shared/constants/pathnames';
import { buildInviteLoginPath } from '@/shared/lib/invite/build-invite-login-path';
import { teamRegistrationSchema, type TeamRegistrationFormData } from '@/shared/lib/validation/team-registration';
import { registerViaInvite } from '../api/register-via-invite';
import { mapTeamSignupError } from './map-team-signup-error';


type UseTeamSignupParams = {
    /** Адрес из ссылки-приглашения: подставляется в форму и остаётся редактируемым. */
    email: string | null;
    token: string;
};


export const useTeamSignup = ({ email, token }: UseTeamSignupParams) => {
    const router                                = useRouter();
    const [ submitting, setSubmitting ]         = useState(false);
    const [ formError, setFormError ]           = useState<string | null>(null);
    const [ suggestLogin, setSuggestLogin ]     = useState(false);
    const form                                  = useForm<TeamRegistrationFormData>({
        resolver      : zodResolver(teamRegistrationSchema),
        mode          : 'onBlur',
        reValidateMode: 'onChange',
        defaultValues : {
            email     : email ?? '',
            password  : '',
            terms     : false,
            first_name: '',
            last_name : '',
            job_title : '',
            phone     : '',
        },
    });

    const onSubmit = form.handleSubmit(async ({ email: address, password, first_name, last_name, job_title, phone }) => {
        setSubmitting(true);
        setFormError(null);
        setSuggestLogin(false);

        try {
            await registerViaInvite({
                invite_token: token,
                email       : address,
                password,
                first_name,
                last_name,
                job_title,
                phone,
            });

            router.replace(TEAM_PATHNAME);
        } catch (err) {
            const mapped = mapTeamSignupError(err);

            for (const [ field, message ] of Object.entries(mapped.fieldErrors)) {
                form.setError(field as keyof TeamRegistrationFormData, { message });
            }

            setFormError(mapped.message);
            setSuggestLogin(mapped.suggestLogin);
            setSubmitting(false);
        }
    });

    return {
        form,
        formError,
        loginHref: buildInviteLoginPath(token, email),
        onSubmit,
        submitting,
        suggestLogin,
    };
};
